import { Link } from "react-router-dom";
import { useState } from "react";
import { ArrowRight, Lightbulb, PenLine, Clapperboard, Video, ClipboardCheck, Smartphone, Lock, ChevronDown, Gift } from "lucide-react";
import { Logo } from "@/components/Logo";
import { MissionCards } from "@/components/home/MissionCards";
import { FaqList } from "@/components/FaqList";
import { FAQ } from "@/modules/premiere-video/resources";
import { BtnLink, Eyebrow } from "@/components/kit";
import { BRAND } from "@/config/brand";
import { useProgress } from "@/modules/premiere-video/store";
import { missionStatus } from "@/modules/premiere-video/logic";

const CREATE = [
  { Icon: Lightbulb, t: "Ton concept", d: "Une idée de vidéo claire, construite autour d'un produit de ton quotidien." },
  { Icon: PenLine, t: "Ton script", d: "Un texte naturel en trois temps : accroche, démonstration, conclusion." },
  { Icon: Clapperboard, t: "Ton storyboard", d: "Quatre plans préparés pour filmer sans hésiter." },
  { Icon: Video, t: "Ta vidéo", d: "Une première vidéo d'entraînement, filmée et montée au smartphone." },
  { Icon: ClipboardCheck, t: "Ton bilan", d: "Une auto-évaluation pour savoir quoi garder et quoi améliorer." },
];

const STEPS = [
  { t: "Tu avances dans les missions", d: "Sept missions courtes, avec des explications simples et des exemples concrets." },
  { t: "Tu construis ta vidéo étape par étape", d: "Chaque mission produit un élément : ton produit, ton idée, ton script, tes plans." },
  { t: "Tu filmes et vérifies ton résultat", d: "Tu tournes avec ton téléphone, tu montes, puis tu fais ton bilan." },
];

function Header() {
  return (
    <>
    <div className="relative z-10 border-b border-white/10">
      <div className="mx-auto flex max-w-6xl items-center justify-between gap-3 px-5 py-2 text-xs text-white/70 sm:px-8">
        <Link to="/" className="inline-flex items-center gap-2 hover:text-white" data-testid="ugcgo-platform-link">
          <img src="/brand/ugcco-icon.png" alt="" className="h-5 w-auto" />Une application UGC & CO
        </Link>
        <Link to="/applications" className="hidden hover:text-white sm:inline" data-testid="ugcgo-platform-apps">Toutes les applications</Link>
      </div>
    </div>
    <header className="relative z-10 mx-auto flex max-w-6xl items-center justify-between px-5 py-5 sm:px-8">
      <Logo light />
      <nav className="hidden items-center gap-8 text-sm text-white/80 md:flex" aria-label="Navigation accueil">
        <a href="#creer" className="transition-colors hover:text-white" data-testid="home-nav-create">Ce que tu vas créer</a>
        <a href="#fonctionnement" className="transition-colors hover:text-white" data-testid="home-nav-how">Comment ça marche</a>
        <a href="#parcours" className="transition-colors hover:text-white" data-testid="home-nav-journey">Le parcours</a>
        <Link to="/sign-in" className="transition-colors hover:text-white" data-testid="home-nav-sign-in">Se connecter</Link>
      </nav>
      <div className="flex items-center gap-3">
        <Link to="/sign-in" className="whitespace-nowrap text-sm font-medium text-white/85 transition-colors hover:text-white md:hidden" data-testid="home-mobile-sign-in">Se connecter</Link>
        <BtnLink to="/parcours" variant="light" size="sm" className="whitespace-nowrap" data-testid="home-my-journey-button">Mon parcours</BtnLink>
      </div>
    </header>
    </>
  );
}

function Hero() {
  const { state } = useProgress();
  const started = [1, 2, 3, 4, 5, 6, 7].some((id) => missionStatus(state, id) !== "todo");
  return (
    <section className="grain relative overflow-hidden rounded-b-[32px] bg-bx text-white sm:rounded-b-[48px]">
      <div className="pointer-events-none absolute -right-40 -top-40 h-[520px] w-[520px] rounded-full border border-gold/20" aria-hidden />
      <div className="pointer-events-none absolute -right-20 -top-20 h-[360px] w-[360px] rounded-full border border-gold/10" aria-hidden />
      <Header />
      <div className="relative z-10 mx-auto grid max-w-6xl items-center gap-12 px-5 pb-16 pt-8 sm:px-8 md:pb-24 md:pt-14 lg:grid-cols-[1.25fr_1fr]">
        <div className="stagger">
          <p className="flex items-center gap-3 text-xs font-semibold uppercase tracking-[0.25em] text-gold">
            <span className="h-px w-8 bg-gold" aria-hidden />Le début de ton aventure UGC
          </p>
          <h1 className="mt-6 text-4xl font-semibold leading-[1.08] tracking-tight sm:text-5xl lg:text-6xl">
            Ta première vidéo UGC <em className="font-medium text-gold">commence ici.</em>
          </h1>
          <p className="mt-6 text-lg font-medium text-white/95 sm:text-xl">7 missions. Un produit. Une idée. Ta première création.</p>
          <div className="mt-5 max-w-xl space-y-3 text-base leading-relaxed text-white/80">
            <p>Tu veux te lancer dans l'UGC mais tu ne sais pas par où commencer ?</p>
            <p>UGC GO t'accompagne étape par étape pour transformer un produit du quotidien en une première vidéo UGC d'entraînement.</p>
            <p>Pas besoin de matériel professionnel ni de milliers d'abonnés.</p>
          </div>
          <div className="mt-9 flex flex-col gap-4 sm:flex-row sm:items-center">
            <BtnLink to="/parcours" variant="light" size="lg" data-testid="hero-start-button" className="group whitespace-nowrap">
              {started ? "Reprendre mon parcours" : "Je commence mon parcours"}              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </BtnLink>
            <p className="text-sm text-white/70" data-testid="hero-price-mention">
              Parcours complet · <span className="font-semibold text-gold">9 €</span> en paiement unique, sans abonnement
            </p>
          </div>
        </div>
        <div className="relative mx-auto w-full max-w-sm lg:max-w-none">
          <div className="relative rounded-[28px] bg-ivory p-6 shadow-[0_30px_80px_-30px_rgba(0,0,0,0.55)] ring-1 ring-gold/40 animate-fade-up">
            <img src={BRAND.logoFull} alt="Emblème UGC GO" className="mx-auto aspect-square w-full max-w-[320px] object-contain" />
            <p className="mt-2 text-center font-display text-lg italic text-bx">{BRAND.tagline}</p>
          </div>
          <div className="absolute -left-3 -top-5 rounded-2xl bg-white px-4 py-3 text-ink shadow-xl sm:-left-8">
            <p className="text-xs uppercase tracking-[0.18em] text-soft">Parcours</p>
            <p className="font-display text-lg text-bx">Ma première vidéo UGC</p>
          </div>
        </div>
      </div>
    </section>
  );
}

function CreateSection() {
  return (
    <section id="creer" className="mx-auto max-w-6xl scroll-mt-8 px-5 py-20 sm:px-8 md:py-28">
      <Eyebrow>À la fin du parcours</Eyebrow>
      <h2 className="mt-4 max-w-xl text-3xl font-semibold text-bx sm:text-4xl">Ce que tu vas créer</h2>
      <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-6">
        {CREATE.map(({ Icon, t, d }, i) => (
          <div key={t} data-testid={`create-card-${i + 1}`} className={`group rounded-[20px] border border-line bg-white p-6 transition-[transform,box-shadow] duration-300 hover:-translate-y-1 hover:shadow-[0_18px_40px_-20px_rgba(101,31,56,0.35)] ${i < 2 ? "lg:col-span-3" : "lg:col-span-2"}`}>
            <div className="flex items-center justify-between">
              <span className="flex h-11 w-11 items-center justify-center rounded-full bg-bx/[0.06] text-bx"><Icon className="h-5 w-5" strokeWidth={1.5} /></span>
              <span className="font-display text-sm text-gold">0{i + 1}</span>
            </div>
            <h3 className="mt-6 text-xl font-medium text-ink">{t}</h3>
            <p className="mt-2 text-sm leading-relaxed text-soft">{d}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

function HowSection() {
  return (
    <section id="fonctionnement" className="scroll-mt-8 border-y border-line bg-white">
      <div className="mx-auto grid max-w-6xl gap-12 px-5 py-20 sm:px-8 md:py-28 lg:grid-cols-[1fr_1.6fr]">
        <div>
          <Eyebrow>La méthode</Eyebrow>
          <h2 className="mt-4 text-3xl font-semibold text-bx sm:text-4xl">Comment ça marche ?</h2>
          <p className="mt-4 max-w-sm text-soft">Pas de théorie interminable : tu apprends en faisant, une mission après l'autre.</p>
        </div>
        <ol className="space-y-8">
          {STEPS.map((s, i) => (
            <li key={s.t} className="flex gap-6 border-b border-line pb-8 last:border-0 last:pb-0" data-testid={`how-step-${i + 1}`}>
              <span className="font-display text-5xl font-medium leading-none text-gold">{i + 1}</span>
              <div>
                <h3 className="text-xl font-medium text-ink">{s.t}</h3>
                <p className="mt-2 leading-relaxed text-soft">{s.d}</p>
              </div>
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
}

function MissionsPreview() {
  return (
    <section id="parcours" className="mx-auto max-w-6xl scroll-mt-8 px-5 py-20 sm:px-8 md:py-28">
      <Eyebrow>Le parcours</Eyebrow>
      <h2 className="mt-4 text-3xl font-semibold text-bx sm:text-4xl">Sept missions, une vidéo</h2>
      <MissionCards />
      <div className="mt-12 flex justify-center">
        <BtnLink to="/parcours" size="lg" className="group whitespace-nowrap" data-testid="home-missions-cta">
          Je crée ma première vidéo
          <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
        </BtnLink>
      </div>
    </section>
  );
}

const KEY_FAQ = ["Faut-il montrer son visage ?", "Faut-il du matériel professionnel ?", "Puis-je gagner de l'argent avec l'UGC ?"]
  .map((q) => FAQ.find((f) => f.q === q)).filter(Boolean);

function HomeFaq() {
  const [all, setAll] = useState(false);
  return (
    <section id="questions" className="mx-auto grid max-w-6xl gap-10 px-5 pb-20 sm:px-8 md:pb-28 lg:grid-cols-[1fr_1.6fr]" data-testid="home-faq">
      <div>
        <Eyebrow>Avant de te lancer</Eyebrow>
        <h2 className="mt-4 text-3xl font-semibold text-bx sm:text-4xl">Tes questions, nos réponses</h2>
        <p className="mt-4 max-w-sm text-base leading-relaxed text-soft">
          {all ? "Toutes les questions que se posent les débutantes avant de commencer." : "Les trois questions que les débutantes se posent le plus souvent avant de commencer."}
        </p>
      </div>
      <div>
        <FaqList key={all ? "all" : "key"} prefix="home-faq" items={all ? FAQ : KEY_FAQ} />
        <button type="button" onClick={() => setAll(!all)} aria-expanded={all} data-testid="home-faq-toggle-all"
          className="group mt-5 inline-flex items-center gap-2 text-sm font-semibold text-bx underline-offset-4 hover:underline">
          {all ? "Afficher moins de questions" : `Voir toutes les questions (${FAQ.length})`}
          <ChevronDown className={`h-4 w-4 transition-transform duration-300 ${all ? "rotate-180" : ""}`} />
        </button>
      </div>
    </section>
  );
}

function FinalCta() {
  return (
    <section className="mx-auto max-w-6xl px-5 pb-20 sm:px-8">
      <div className="grain relative overflow-hidden rounded-[28px] bg-bx px-6 py-14 text-white sm:px-14">
        <div className="relative z-10 flex flex-col gap-8 md:flex-row md:items-end md:justify-between">
          <div>
            <h2 className="max-w-lg text-3xl font-semibold sm:text-4xl">De l'idée à ta première vidéo, pas à pas.</h2>
            <ul className="mt-6 space-y-2 text-sm text-white/80">
              <li className="flex items-center gap-2"><Smartphone className="h-4 w-4 text-gold" strokeWidth={1.5} />Ton smartphone suffit</li>
              <li className="flex items-center gap-2"><Lock className="h-4 w-4 text-gold" strokeWidth={1.5} />Tes réponses sont enregistrées dans ton compte</li>
            </ul>
          </div>
          <div className="flex flex-col items-start gap-4 md:items-end">
            <BtnLink to="/parcours" variant="light" size="lg" data-testid="final-cta-button">Je commence mon parcours<ArrowRight className="h-4 w-4" /></BtnLink>
            <Link to="/offrir" className="inline-flex items-center gap-2 text-sm text-white/80 underline-offset-4 transition-colors hover:text-white hover:underline" data-testid="home-gift-link">
              <Gift className="h-4 w-4 text-gold" strokeWidth={1.5} />Offrir le parcours à une amie
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}

export default function Home() {
  return (
    <div className="min-h-screen bg-ivory" data-testid="home-page">
      <Hero />
      <CreateSection />
      <HowSection />
      <MissionsPreview />
      <HomeFaq />
      <FinalCta />
      <footer className="border-t border-line">
        <div className="mx-auto flex max-w-6xl flex-col gap-4 px-5 py-8 text-sm text-soft sm:flex-row sm:items-center sm:justify-between sm:px-8">
          <Logo testId="footer-logo" />
          <p>UGC GO ne promet ni collaboration rémunérée, ni revenu : il t'accompagne pour créer ta première vidéo d'entraînement.</p>
        </div>
      </footer>
    </div>
  );
}
